import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiGet } from '@/context/AuthContext';
import { LayoutDashboard, Mail, Users, UserCheck, Music, Beer, Download, Shield, LogOut, Menu, X, Ticket, Table2, CheckCircle, Image, Gift, Presentation, Activity, Bot, FileText, Radio } from 'lucide-react';const LOGO_URL = "/logo.png";

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Overblik', end: true },
  { path: '/admin/invitations', icon: Ticket, label: 'Invitationer' },
  { path: '/admin/guests', icon: Users, label: 'Gæster' },
  { path: '/admin/rsvp', icon: UserCheck, label: 'RSVP' },
  { path: '/admin/checkin', icon: CheckCircle, label: 'Check-in' },
  { path: '/admin/tables', icon: Table2, label: 'Borde' },
  { path: '/admin/emails', icon: Mail, label: 'Mailcenter' },
  { path: '/admin/music', icon: Music, label: 'Musikønsker' },
  { path: '/admin/drinks', icon: Beer, label: 'Bar' },
  { path: '/admin/wishes', icon: Gift, label: 'Ønskeliste' },
  { path: '/admin/images', icon: Image, label: 'Billeder' },
  { path: '/admin/slideshow', icon: Presentation, label: 'Diasshow' },
  { path: '/live', icon: Radio, label: 'Livevisning' },
  { path: '/admin/audit', icon: Activity, label: 'Aktivitetslog' },
  { path: '/admin/content', icon: FileText, label: 'Indhold' },
  { path: '/admin/ai', icon: Bot, label: 'AI' },
  { path: '/admin/export', icon: Download, label: 'Eksport' },
];

export default function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [barActive, setBarActive] = useState(0);

  useEffect(() => {
    const loadStats = () => {
      apiGet('/api/admin/dashboard')
        .then((r) => {
          const s = r.data.stats || {};
          setBarActive((s.bar_pending || 0) + (s.bar_preparing || 0) + (s.bar_ready || 0));
        })
        .catch(() => {});
    };
    loadStats();
    const id = setInterval(loadStats, 15000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#020617] flex overflow-x-hidden">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-slate-900/95 backdrop-blur-xl border-r border-slate-800 transform transition-transform duration-200 lg:translate-x-0 lg:static lg:inset-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="admin-sidebar">
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Molin30" className="h-8" />
            <span className="text-xs font-bold uppercase tracking-wider text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded">Admin</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {navItems.map(({ path, icon: Icon, label, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-purple-600/10 text-purple-400 border border-purple-500/20'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, '-')}-link`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {label === 'Bar' && barActive > 0 && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300">{barActive}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 w-full transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Log ud
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 flex items-center px-4 lg:px-6">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden mr-3 text-slate-400 hover:text-white" data-testid="admin-sidebar-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-slate-300">Administration</span>
          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-x-hidden p-4 lg:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
